/**
 * LoteCard — card-lote ATÔMICO do board da Produção v2 (Lote C — §2.4).
 *
 * De PLANO_CORTE até USINAGEM o que navega no board é o LOTE (o otimizador
 * mistura as peças de todos os ambientes nas mesmas chapas — não existe
 * "onde está cada ambiente" dentro de F4/F5). Este card é a entidade
 * agregadora, visualmente distinta do AmbienteCard: trilho accent à
 * esquerda + ícone de camadas + número LT em mono.
 *
 * Anatomia em 3 faixas:
 *   1. Header — FiLayers + `LT-UN1-N` (mono) + chip de origem
 *      (PharuCut / Manual) + chip de sobra %.
 *   2. Corpo  — "N ambientes" + lista compacta dos membros
 *      (nome · pedido, até 3 + "+K") quando `ambientes` está presente;
 *      na VERSÃO CEGA (sem `lote:view` o backend omite os membros) só o
 *      contador — nomes de clientes nunca vazam entre pedidos (I-8).
 *   3. Rodapé — pior buffer entre os membros (chip na cor da zona),
 *      Σ peças, ❄ congelados (quando > 0) e badge warning
 *      "dessincronizado" (etapa de membro divergiu da etapa do lote).
 *
 * Duas densidades como o AmbienteCard: "full" e "compact" (colunas do
 * board). Clique abre /producao/lotes/{id} (navegação é do caller).
 *
 * @param {object}  props
 * @param {string}  props.numero            Código do lote (LT-UN1-4).
 * @param {string}  [props.origem]          "DASHCUT" | "EXTERNO".
 * @param {number|string} [props.sobraPercentual] Sobra % do plano (null = sem plano).
 * @param {Array|null} [props.ambientes]    Membros [{id, numero, nome,
 *   pedido_numero, cliente_nome}] — AUSENTE/null na versão cega.
 * @param {number}  [props.ambientesCount]  Nº de ambientes do lote.
 * @param {number}  [props.bufferPct]       Pior consumo de buffer (%).
 * @param {"verde"|"amarelo"|"vermelho"} [props.bufferZona] Zona do pior buffer.
 * @param {number}  [props.pecasTotal]      Σ peças do lote.
 * @param {number}  [props.congeladosCount] Nº de membros congelados.
 * @param {boolean} [props.dessincronizado] Etapa de membro diverge da do lote.
 * @param {"full"|"compact"} [props.densidade] Densidade do card.
 * @param {Function} [props.onClick]        Card inteiro clicável.
 */
import React from "react";
import { FiBox, FiCloudSnow, FiLayers } from "react-icons/fi";
import styled, { css } from "styled-components";

const MAX_MEMBROS_VISIVEIS = 3;

const COR_ZONA_BUFFER = {
  verde: { bg: "var(--success-bg)", texto: "var(--success-text)" },
  amarelo: { bg: "var(--warning-bg)", texto: "var(--warning-text)" },
  vermelho: { bg: "var(--danger-bg)", texto: "var(--danger-text)" },
};

const ORIGEM_CHIP = {
  DASHCUT: "PharuCut",
  EXTERNO: "Manual",
};

const Cartao = styled.article`
  background: var(--surface);
  border: 1px solid var(--border);
  /* Acento próprio do LOTE (entidade agregadora) — identidade, não estado:
     o trilho accent diferencia o card-lote do AmbienteCard à primeira
     vista, na mesma família visual. */
  border-left: 3px solid var(--accent);
  border-radius: 14px;
  box-shadow: var(--shadow-1);
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  min-width: 0;

  ${({ $compact }) =>
    $compact &&
    css`
      padding: 11px 12px;
      gap: 7px;
      border-radius: 12px;
    `}

  ${({ $clicavel }) =>
    $clicavel &&
    css`
      cursor: pointer;
      &:hover {
        border-color: var(--border-strong);
        border-left-color: var(--accent);
        box-shadow: var(--shadow-2);
      }
      &:focus-visible {
        outline: none;
        box-shadow: var(--shadow-focus);
      }
    `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;

  .ic {
    display: flex;
    flex-shrink: 0;
    color: var(--accent);
  }
`;

const Numero = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: ${({ $compact }) => ($compact ? "13px" : "14.5px")};
  font-weight: 600;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipsHeader = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const ChipOrigem = styled.span`
  font-size: 10px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: var(--radius-pill, 999px);
  white-space: nowrap;

  ${({ $dashcut }) =>
    $dashcut
      ? css`
          background: var(--accent-soft);
          color: var(--accent);
        `
      : css`
          border: 1px solid var(--border-strong);
          color: var(--ink-3);
        `}
`;

const ChipSobra = styled.span`
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  padding: 2px 7px;
  border-radius: var(--radius-pill, 999px);
  border: 1px solid var(--border-strong);
  color: var(--ink-3);
  white-space: nowrap;
`;

const ContagemAmbientes = styled.div`
  font-size: 12px;
  color: var(--ink-2);

  strong {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: var(--ink);
  }
`;

const ListaMembros = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Membro = styled.li`
  font-size: ${({ $compact }) => ($compact ? "11.5px" : "12px")};
  color: var(--ink-3);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .nome {
    color: var(--ink-2);
  }
  .pedido {
    font-variant-numeric: tabular-nums;
  }
`;

const MembrosExtra = styled.li`
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-4);
`;

const Rodape = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? "10px" : "14px")};
  flex-wrap: wrap;
  font-size: 12px;
  color: var(--ink-3);
  padding-top: 2px;
  border-top: 1px solid var(--border-subtle);
`;

const ChipBuffer = styled.span`
  font-size: 10.5px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  padding: 2px 8px;
  border-radius: var(--radius-pill, 999px);
  white-space: nowrap;
  background: ${({ $zona }) =>
    (COR_ZONA_BUFFER[$zona] || COR_ZONA_BUFFER.verde).bg};
  color: ${({ $zona }) =>
    (COR_ZONA_BUFFER[$zona] || COR_ZONA_BUFFER.verde).texto};
`;

const Metrica = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  color: ${({ $alerta }) => ($alerta ? "var(--warning-text)" : "inherit")};

  svg {
    font-size: 14px;
    color: ${({ $alerta }) =>
      $alerta ? "var(--warning-text)" : "var(--ink-4)"};
  }
`;

const Mono = styled.span`
  font-variant-numeric: tabular-nums;
`;

const BadgeDessincronizado = styled.span`
  margin-left: auto;
  font-size: 10px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: var(--radius-pill, 999px);
  background: var(--warning-bg);
  color: var(--warning-text);
  white-space: nowrap;
`;

function LoteCard({
  numero,
  origem,
  sobraPercentual,
  ambientes,
  ambientesCount,
  bufferPct = 0,
  bufferZona = "verde",
  pecasTotal,
  congeladosCount = 0,
  dessincronizado = false,
  densidade = "full",
  onClick,
}) {
  const compact = densidade === "compact";
  const clicavel = typeof onClick === "function";

  // Versão cega (I-8): sem `lote:view` o backend OMITE os membros — o card
  // mostra só o contador, sem nome de cliente/ambiente de outros pedidos.
  const membros = Array.isArray(ambientes) ? ambientes : null;
  const totalAmbientes = ambientesCount ?? membros?.length ?? 0;
  const visiveis = membros ? membros.slice(0, MAX_MEMBROS_VISIVEIS) : [];
  const extras = membros ? membros.length - visiveis.length : 0;

  const sobraNum = Number(sobraPercentual);
  const temSobra =
    sobraPercentual !== null
    && sobraPercentual !== undefined
    && sobraPercentual !== ""
    && Number.isFinite(sobraNum);

  const origemChip = ORIGEM_CHIP[origem] || null;

  return (
    <Cartao
      $compact={compact}
      $clicavel={clicavel}
      onClick={onClick}
      role={clicavel ? "button" : undefined}
      tabIndex={clicavel ? 0 : undefined}
      onKeyDown={
        clicavel
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      aria-label={clicavel ? `Abrir lote ${numero}` : undefined}
    >
      <Header>
        <span className="ic" aria-hidden="true">
          <FiLayers size={compact ? 14 : 16} />
        </span>
        <Numero $compact={compact} title={`Lote ${numero}`}>
          {numero}
        </Numero>
        <ChipsHeader>
          {origemChip && (
            <ChipOrigem
              $dashcut={origem === "DASHCUT"}
              title={
                origem === "DASHCUT"
                  ? "Plano de corte otimizado pelo PharuCut"
                  : "Plano de corte registrado manualmente (otimização externa)"
              }
            >
              {origemChip}
            </ChipOrigem>
          )}
          {temSobra && (
            <ChipSobra
              title={`Sobra/refilo do plano de corte: ${sobraNum.toLocaleString(
                "pt-BR",
                { maximumFractionDigits: 1 },
              )}%`}
            >
              sobra{" "}
              {sobraNum.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
            </ChipSobra>
          )}
        </ChipsHeader>
      </Header>

      <ContagemAmbientes
        title={
          membros
            ? undefined
            : "A identificação dos ambientes deste lote é restrita (permissão de lote)"
        }
      >
        <strong>{totalAmbientes}</strong>{" "}
        {totalAmbientes === 1 ? "ambiente" : "ambientes"}
      </ContagemAmbientes>

      {membros && visiveis.length > 0 && (
        <ListaMembros>
          {visiveis.map((m) => (
            <Membro
              key={m.id || `${m.numero}-${m.pedido_numero}`}
              $compact={compact}
              title={`${m.nome || m.numero || "Ambiente"} · ${
                m.pedido_numero || "–"
              }${m.cliente_nome ? ` · ${m.cliente_nome}` : ""}`}
            >
              <span className="nome">{m.nome || m.numero || "Ambiente"}</span>
              {" · "}
              <span className="pedido">{m.pedido_numero || "–"}</span>
            </Membro>
          ))}
          {extras > 0 && <MembrosExtra>+{extras}</MembrosExtra>}
        </ListaMembros>
      )}

      <Rodape $compact={compact}>
        <ChipBuffer
          $zona={bufferZona}
          title={`Pior buffer entre os ambientes do lote: ${bufferPct}% (zona ${bufferZona})`}
        >
          buf {bufferPct}%
        </ChipBuffer>
        <Metrica title={`${pecasTotal ?? 0} peças no lote`}>
          <FiBox aria-hidden="true" />
          <Mono>{pecasTotal ?? 0}</Mono>
        </Metrica>
        {congeladosCount > 0 && (
          <Metrica
            $alerta
            title={`${congeladosCount} ambiente(s) congelado(s) no lote — pulados na conclusão em bloco`}
          >
            <FiCloudSnow aria-hidden="true" />
            <Mono>{congeladosCount}</Mono>
          </Metrica>
        )}
        {dessincronizado && (
          <BadgeDessincronizado title="A etapa de algum ambiente membro diverge da etapa do lote (avanço registrado fora do lote) — concluir a etapa do lote realinha os membros aplicáveis">
            dessincronizado
          </BadgeDessincronizado>
        )}
      </Rodape>
    </Cartao>
  );
}

export default LoteCard;
